import {Injectable} from '@angular/core';
import {DateTime} from 'luxon';
import {Subscription} from 'rxjs';
import {getShortUUID, makeGuiFriendlyExpirationDateDte} from '../../utils';
import {MessageBusService} from '../../message-bus.service';
import {ShellClientService} from '../../shell-communication/shell-client.service';
import {OrionSettingsService} from '../../orion-settings-dialog/orion-settings.service';
import {IMavenRunSkill, MavenRunRequest, MavenRunResult, MavenStageEvent} from './maven-run-skill';
import {mavenRangeMode, MavenMaxRisk, MavenMinProfit, MavenSideInputs} from '../maven-domain';
import {isOverrideComplete, MarketConditionOverrides} from '../../copilot-shared/opg-copilot-domain';
import {OpgCoPilotMarketOverrideDto} from '../../copilot-shared/opg-copilot-operations';
import {MAVEN_GENERIC_ERROR, resolveMavenRefusal} from './maven-refusal-messages';
import {
    MAVEN_CONTRACT_VERSION,
    MAVEN_OUTCOME_EMPTY,
    MAVEN_OUTCOME_PRE_SEND_BLOCKED,
    MAVEN_OUTCOME_SUCCEEDED,
    MAVEN_OUTCOME_SUPERSEDED,
    MAVEN_OUTCOME_TERMINAL_FAILED,
    MAVEN_RESULT_READY_MESSAGE_TYPE,
    MAVEN_RUN_STAGE_MESSAGE_TYPE,
    MavenCardDto,
    MavenResultReadyDto,
    MavenRunStageDto,
    MavenSideInputsDto,
    RunMavenSkill
} from './maven-operations';

// THE REAL MAVEN RUN CLIENT (CR-20260910-001 milestone 4, contract M1 v0.3).
//
// Send RunMavenSkill (the HTTP call returns an ack), receive MavenRunStageDto several times and
// MavenResultReadyDto exactly once over the ESS WebSocket, correlate by runId. The transport
// rules are the ORION (OPG) client's, and they are rules rather than habits:
//
//   SUBSCRIBE BEFORE SEND. The pushes ride the WebSocket while the ack is a separate HTTP call.
//   Subscribing first removes the "push beat the ack" race outright.
//
//   ONE TEARDOWN CHOKE POINT. Every settle path — the delivery, the timer, a rejected ack, even
//   processCommand throwing synchronously — funnels through settle(), which removes the map
//   entry, closes BOTH subscriptions and kills the timer before completing the promise. A second
//   settle for the same runId finds no entry and is a no-op. Nothing can outlive its run.
//
//   THE TIMER IS A REAPER, NOT A DEADLINE. The backend owns the 120s run budget and answers
//   within it. This timer exists for runs nothing will EVER push to — a dead shell, a displaced
//   run the backend went silent on — and sits above the budget so the backend's own answer
//   always wins the race.
//
// EVERY OUTCOME RESOLVES. The donor rejects on failure; this one does not, on purpose. Maven
// renders a refusal IN PLACE beside the control that caused it, so the caller needs the outcome
// and the detail as data, not as an exception to catch and re-derive. A run that fails is still
// a run that answered.
//
// NO CANCEL, NO STOP (ruling R-D). Pressing Apply again starts a new run; the backend pushes
// 'superseded' to the displaced one exactly once, and that push settles it here like any other.
//
// ES5 note: the pending map is a plain object keyed by runId; arrays + indexOf only.

// The grace above the BACKEND's run budget. The budget itself is a shell-level ORION setting
// (leader ruling L9) — tuned on measurement, which is why it is a setting and not a constant — so
// this client reads it rather than holding a second copy of the number. The fallback exists only
// for the moment before the setting has loaded.
export const MAVEN_TEARDOWN_GRACE_MS = 10000;
export const MAVEN_FALLBACK_RUN_BUDGET_MS = 120000;

interface PendingRun {
    stageSubscription: Subscription;
    resultSubscription: Subscription;
    timerHandle: any;
    onStage: (event: MavenStageEvent) => void;
    resolve: (result: MavenRunResult) => void;
}

@Injectable()
export class MavenRunClient implements IMavenRunSkill {

    constructor(
        private readonly _messageBus: MessageBusService,
        private readonly _shellClient: ShellClientService,
        private readonly _orionSettings: OrionSettingsService
    ) {}

    // The reaper's deadline, ALWAYS above the backend's own budget so the backend's answer wins
    // the race. If this fired first it would turn a slow-but-healthy run into a failure the
    // trader has to re-run, and he would never learn why.
    get teardownMs(): number {
        const seconds = this._orionSettings ? this._orionSettings.mavenRunBudgetSeconds : 0;
        const budget = (typeof seconds === 'number' && isFinite(seconds) && seconds > 0)
            ? seconds * 1000
            : MAVEN_FALLBACK_RUN_BUDGET_MS;
        return budget + MAVEN_TEARDOWN_GRACE_MS;
    }

    private readonly _pending: { [runId: string]: PendingRun } = {};

    // Read by the karma leak assertions after every settle path: a client that answered correctly
    // but kept its subscription is still a defect.
    get pendingRunCount(): number {
        return Object.keys(this._pending).length;
    }

    run(request: MavenRunRequest, onStage: (event: MavenStageEvent) => void): Promise<MavenRunResult> {
        return new Promise<MavenRunResult>((resolve) => {

            const runId = getShortUUID();
            const defaultQty = request.defaultQty;

            // (1) SUBSCRIBE BEFORE SEND — both streams. Foreign runIds fall through the
            // correlation check in each handler.
            const stageSubscription = this._messageBus
                .of<MavenRunStageDto>(MAVEN_RUN_STAGE_MESSAGE_TYPE)
                .subscribe(m => this.onStagePush(runId, m ? m.payload : null));

            const resultSubscription = this._messageBus
                .of<MavenResultReadyDto>(MAVEN_RESULT_READY_MESSAGE_TYPE)
                .subscribe(m => this.onResultPush(runId, m ? m.payload : null));

            // (2) the reaper.
            const timerHandle = setTimeout(() => {
                this.settle(runId, entry => entry.resolve({
                    outcome: 'terminalFailed',
                    detail: MAVEN_GENERIC_ERROR
                }));
            }, this.teardownMs);

            this._pending[runId] = {stageSubscription, resultSubscription, timerHandle, onStage, resolve};

            // (3) send. A rejected ack and a synchronous throw both settle through the choke
            // point; neither leaves the entry behind.
            try {
                this._shellClient
                    .processCommand(buildRunCommand(runId, request))
                    .catch((e: any) => this.failFast(runId, e));
            } catch (e) {
                this.failFast(runId, e);
            }
        });
    }

    private failFast(runId: string, e: any): void {
        this.settle(runId, entry => entry.resolve({outcome: 'terminalFailed', detail: MAVEN_GENERIC_ERROR}));
    }

    // Stage pushes do NOT settle the run (guarantee G6: they are not deliveries). They are
    // forwarded to the caller and the entry stays open for its one delivery.
    private onStagePush(runId: string, dto: MavenRunStageDto | null): void {
        if (!dto || dto.runId !== runId) {
            return;
        }
        const entry = this._pending[runId];
        if (!entry) {
            return;   // already settled — a stage arriving after the delivery is ignored, not shown
        }
        entry.onStage({stage: dto.stage, detail: dto.detail});
    }

    // THE one delivery. Every outcome resolves; the caller decides what to render. The wire's
    // flat DTO is narrowed HERE onto the union, so the flat shape never escapes the transport —
    // which is what makes the outcome impossible to ignore upstream (ruling L46).
    private onResultPush(runId: string, dto: MavenResultReadyDto | null): void {
        if (!dto || dto.runId !== runId) {
            return;   // not this run's push
        }
        this.settle(runId, entry => entry.resolve(toRunResult(dto)));
    }

    // THE teardown choke point. Remove the entry, close both subscriptions, kill the timer, THEN
    // complete. Idempotent by construction: a second call finds no entry.
    private settle(runId: string, complete: (entry: PendingRun) => void): void {
        const entry = this._pending[runId];
        if (!entry) {
            return;
        }
        delete this._pending[runId];
        entry.stageSubscription.unsubscribe();
        entry.resultSubscription.unsubscribe();
        clearTimeout(entry.timerHandle);
        complete(entry);
    }
}

// ---- Delivery mapping (wire -> the union) ---------------------------------------------------

// An outcome OFF the contract's closed set is a DEFECT (guarantee G12), and it FAILS CLOSED here
// as a terminal failure that names the code. Treating an unknown outcome as success would draw
// whatever cards happened to be attached to it.
// ---- The ESS reviver turns our calendar LABELS into instants -----------------------------------
//
// ⚠ EVERY ESS FRAME IS PARSED WITH `customJsonParser`, and that reviver converts ANY json key whose
// lowercased name contains "date" or "time" into a JS Date at UTC midnight. Maven's wire calls the
// package's expiration `date` and each nearby report's day `date`, so both arrive as Date objects
// rather than the 'yyyy-MM-dd' strings the contract declares. TypeScript cannot see this: the
// declared type says string and the runtime value is a Date, and the first thing to touch it threw
// `input.match is not a function` from inside Luxon.
//
// THE PLATFORM ALREADY HAS A RULE FOR THIS and Maven did not follow it: the wire NAMES avoid the
// trigger words (`...DayKey`) and a mapper restores the contract's names — see the comment at
// shell-operations-protocol.ts:2745, which ends "Do NOT rename them to '...Date'".
//
// ⚠ THIS IS THE MAPPER HALF ONLY. The wire fields are still called `date`, so the reviver still
// converts them and this function still has to undo it. Renaming them to `dayKey` end to end is the
// real fix and is recorded as follow-on work; it was not done in the same change because no type
// check is available to catch a missed reference, and a silently missed rename is worse than a
// normalisation that runs in one place.
//
// Normalising back is exact: the reviver read the label at UTC, so reading it back at UTC returns
// the same calendar day. A value that is already a string is passed through untouched.
function toDayKey(value: any): any {
    if (value instanceof Date) {
        return DateTime.fromJSDate(value, {zone: 'UTC'}).toFormat('yyyy-MM-dd');
    }
    return value;
}

function restoreDayKeys(cards: MavenCardDto[]): MavenCardDto[] {
    if (!cards) {
        return [];
    }

    return cards.map(card => {
        if (!card) {
            return card;
        }

        const reports = card.nearbyReports
            ? card.nearbyReports.map(r => r ? {...r, date: toDayKey((r as any).date)} : r)
            : card.nearbyReports;

        return {...card, date: toDayKey((card as any).date), nearbyReports: reports};
    });
}

// ---- The jagged array protobuf cannot carry ----------------------------------------------------
//
// ⚠ THE HEDGE POP-UP OPENED WITH NO ORDERS IN IT, and this is why. Its table renders
// `orderGroup.balancedOrders` — a JAGGED array, one row group per package. On the backend
// `HedgeOrderGroup.BalancedOrders` carries NO [ProtoMember] at all, because protobuf cannot
// serialise a jagged array; `BalancedOrdersProto` is [ProtoMember(4)] and exists for exactly that
// reason, wrapping each inner array in an object.
//
// MAVEN IS THE FIRST CONSUMER TO SEND HedgeStateTransaction OVER PROTOBUF. The Hedging Grid and
// Package Comparison reply over the JSON boundary, where the plain property travels unaided and the
// wrapper is never needed — so the gap existed for as long as the type has, and nothing had stood on
// it. `orderTransactions` itself arrived populated, which is why the dialog opened at all and why
// only the table inside it was empty.
//
// Restored here, at the same delivery boundary that already normalises the revived dates, so the
// panel never sees an unmapped shape and there is one place that knows about the wire's quirks.
// This one MUTATES the deserialised graph rather than copying it, deliberately: the pop-up is handed
// the very objects the card holds, and a copy here would hand it a different set again.
//
// ---------------------------------------------------------------------------------------------
// AND NO, MAVEN DOES NOT WANT THE QUOTE-INDEX HALF — the answer, since it was asked explicitly.
//
// The working consumer of this wrapper also flattens the restored orders into a quote index so they
// re-price live. Maven must NOT copy that, and the reason is worth stating rather than leaving as a
// difference someone later "corrects":
//
//   - THE CARD does not need it. Its hedge figure is combined from `group.orders`, with the tickers
//     subscribed from the card's own flat `tickers[]`. It never reads `balancedOrders` at all.
//
//   - THE POP-UP would be made WORSE by it. Over protobuf `Orders` [ProtoMember(3)] and
//     `BalancedOrdersProto` [ProtoMember(4)] carry the SAME legs twice, so they deserialise into TWO
//     INDEPENDENT OBJECT GRAPHS. The pop-up's table prices each row from `balancedOrders`, while its
//     group total comes from `getOrderGroupTotal(orderGroup, ...)` over `orders`. Today nothing in
//     Maven re-prices either graph, so both hold the delivered prices and the rows agree with the
//     total. Indexing one of them would start ticking that one alone — and the visible result would
//     be a table whose rows no longer add up to their own total.
//
// So the divergence is latent, not active, and the way to make it safe is to price both graphs or
// neither — never one. Raised with the leader as a finding rather than fixed here.
// ---------------------------------------------------------------------------------------------
function restoreBalancedOrders(cards: MavenCardDto[]): MavenCardDto[] {
    if (!cards) {
        return [];
    }

    cards.forEach(card => {
        const transactions = card && card.hedges ? card.hedges.transaction : null;

        if (!transactions) {
            return;
        }

        transactions.forEach(transaction => {
            const groups = transaction ? transaction.orderTransactions : null;

            if (!groups) {
                return;
            }

            groups.forEach(group => {
                if (!group || !group.balancedOrdersProto) {
                    return;
                }

                group.balancedOrders = group.balancedOrdersProto.map(x => x.orderGroup);
            });
        });
    });

    return cards;
}

// ---- A field the backend never carries -----------------------------------------------------------
//
// `CashFlowAdjustment.expirationDatePrettyWithDaysToExpiration` ("18-Sep-26 (5d)") exists on the
// frontend only: the APG derives it from the option chain after pricing
// (adjustment-pricing-grid.component.ts:3592) and Package Comparison from `expiration`
// (package-comparison.component.ts:1765). Maven handed the delivered adjustments to the adjustment
// pop-up with it UNDEFINED, and two things stood on it (vrequest 2026-09-13):
//
//   - the pop-up's Copy-to-ETS path splits it right after the save
//     (adjustment-solution-popup.component.ts:517) — the throw was swallowed and the green
//     "copied" toast never came;
//   - the saved slot's descriptor collapsed to "N/A (<logicalId>)" for EVERY card of the run, so
//     a copy from the second card left the first card's mark lit.
//
// This is not Maven mapping its delivery — legs, prices and shapes pass untouched — it is the
// pop-up's INPUT CONTRACT being completed at the boundary where every other wire quirk already is,
// the same way Package Comparison completes it. Mutates in place for the reason
// restoreBalancedOrders gives: the pop-up must receive the very objects the card holds.
function restoreAdjustmentPrettyDates(cards: MavenCardDto[]): MavenCardDto[] {
    if (!cards) {
        return [];
    }

    cards.forEach(card => {
        const adjustments = card && card.adjustment ? card.adjustment.adjustments : null;

        if (!adjustments) {
            return;
        }

        adjustments.forEach(adjustment => {
            if (!adjustment || adjustment.expirationDatePrettyWithDaysToExpiration) {
                return;
            }

            adjustment.expirationDatePrettyWithDaysToExpiration =
                makeGuiFriendlyExpirationDateDte(adjustment.expiration);
        });
    });

    return cards;
}

export function toRunResult(dto: MavenResultReadyDto): MavenRunResult {
    switch (dto.outcome) {
        case MAVEN_OUTCOME_SUCCEEDED:
            return {
                outcome: 'succeeded',
                // Never re-ordered here. The run's rank is the order (guarantee G8).
                cards: restoreAdjustmentPrettyDates(restoreBalancedOrders(restoreDayKeys(dto.cards))),
                // The BACKEND's figure is authoritative — it is the quantity the normalized prices
                // on every card were divided by. Falling back to the requested one would silently
                // re-base every figure on screen.
                defaultQty: dto.defaultQty,
                // Beside defaultQty, because it is the same kind of thing: a run-level portfolio
                // fact the cards are meaningless without. Taken as delivered, never defaulted.
                strategyType: dto.strategyType,
                // CR-20260914-001: as delivered; an absent field (older backend) is no note.
                expectedRangeNotes: dto.expectedRangeNotes || []
            };
        case MAVEN_OUTCOME_EMPTY:
            return {
                outcome: 'empty',
                sentence: 'Maven found no packages for this setup.',
                expectedRangeNotes: dto.expectedRangeNotes || []
            };
        case MAVEN_OUTCOME_SUPERSEDED:
            return {outcome: 'superseded'};
        case MAVEN_OUTCOME_PRE_SEND_BLOCKED:
            return {outcome: 'preSendBlocked', code: dto.failureDetail || '',
                detail: resolveMavenRefusal(dto.failureDetail || null).message};
        case MAVEN_OUTCOME_TERMINAL_FAILED:
            return {outcome: 'terminalFailed', detail: MAVEN_GENERIC_ERROR};
        default:
            return {
                outcome: 'terminalFailed',
                detail: MAVEN_GENERIC_ERROR
            };
    }
}

// ---- Request mapping (domain -> wire), module-level pure functions --------------------------

function pad2(n: number): string {
    return n < 10 ? '0' + n : '' + n;
}

// 'yyyy-MM-dd' from LOCAL calendar components (ADR-037 / WIRE-01). NEVER toISOString: it converts
// to UTC first, which shifts a picked calendar date across midnight for anyone east or west of it.
// This is the ONLY place in the lane that turns a Date into a wire string.
export function toCalendarDateString(d: Date): string {
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
}

function toCalendarDateStrings(dates: Date[] | null | undefined): string[] {
    const source = dates || [];
    const out: string[] = [];
    for (let i = 0; i < source.length; i++) {
        const d = source[i];
        if (d instanceof Date && !isNaN(d.getTime())) {
            out.push(toCalendarDateString(d));
        }
    }
    return out;
}

interface WireDateFields {
    dateKind: string;
    date: string | null;
    rangeStart: string | null;
    rangeEnd: string | null;
}

// Exactly one variant per dateKind, the other fields null. An unset date passes through as null
// and the backend refuses it deterministically (MissingDate) — this mapper never invents a date.
function mapDateFields(sel: any): WireDateFields {
    if (sel && sel.kind === 'single') {
        return {
            dateKind: 'single',
            date: sel.single instanceof Date ? toCalendarDateString(sel.single) : null,
            rangeStart: null,
            rangeEnd: null
        };
    }
    if (sel && sel.kind === 'range') {
        return {
            dateKind: 'range',
            date: null,
            rangeStart: sel.start instanceof Date ? toCalendarDateString(sel.start) : null,
            rangeEnd: sel.end instanceof Date ? toCalendarDateString(sel.end) : null
        };
    }
    return {dateKind: sel ? sel.kind : '', date: null, rangeStart: null, rangeEnd: null};
}

const MAVEN_CONTRACT_MULTIPLIER = 100;

function usableQty(defaultQty: number): boolean {
    return typeof defaultQty === 'number' && isFinite(defaultQty) && defaultQty > 0;
}

// Normalized -> plain dollars, the ORION (OPG)'s own rule: raw = normalized x defaultQty x 100.
//
// null is a REFUSAL SENTINEL, not a zero: a normalized entry with no usable default quantity
// cannot be converted, and sending a guess would run the engine on a number nobody typed. The
// backend refuses a null or non-positive amount loudly (MalformedSideInputs), which is the right
// surface for it — far better than a silent 0 that the engine would treat as a real answer.
function toPlainDollars(amount: number | null, normalized: boolean, defaultQty: number): number | null {
    if (amount == null || !isFinite(amount)) {
        return null;
    }
    if (!normalized) {
        return amount;
    }
    return usableQty(defaultQty) ? amount * defaultQty * MAVEN_CONTRACT_MULTIPLIER : null;
}

function mapMinProfit(mp: MavenMinProfit, defaultQty: number): number {
    const dollars = toPlainDollars(mp ? mp.amount : null, mp ? mp.normalized : false, defaultQty);
    // 0 is the sentinel the backend rejects by name for min profit (it does not derive a positive
    // cushion), so an unconvertible entry lands there rather than becoming a plausible number.
    return dollars === null ? 0 : dollars;
}

function mapMaxRisk(mr: MavenMaxRisk, defaultQty: number): { kind: string; amount: number | null; level: string | null } {
    if (mr && mr.kind === 'dollar') {
        return {kind: 'dollar', amount: toPlainDollars(mr.amount, mr.normalized, defaultQty), level: null};
    }
    if (mr && mr.kind === 'relative') {
        return {kind: 'relative', amount: null, level: mr.level};
    }
    return {kind: '', amount: null, level: null};
}

// A DISABLED side crosses as null — that is what "the trader left this side empty" means on the
// wire, and the backend refuses NoHedgeSide when both are null (ruling Q7). A side that is
// enabled but incomplete crosses AS IT IS: the courier does not repair it, and the backend refuses
// it loudly by name.
function mapSideInputs(side: MavenSideInputs | null | undefined, defaultQty: number): MavenSideInputsDto | null {
    if (!side || !side.enabled) {
        return null;
    }
    if (side.mode === 'preset') {
        return {mode: 'preset', presetId: side.presetId, offsetFromShorts: 0, coverage: null, minProfitDollars: 0, maxRisk: null};
    }
    return {
        offsetFromShorts: side.offsetFromShorts == null ? 0 : side.offsetFromShorts,
        coverage: {
            kind: side.coverage ? side.coverage.kind : '',
            // The end strike belongs to 'range' alone. A stale value left behind by a mode switch
            // must not ride along on a followThrough or an expectedRange order: it would be a
            // field the trader cannot see answering a question nobody asked.
            endStrike: (side.coverage && side.coverage.kind === 'range' && mavenRangeMode(side.coverage) === 'endStrike')
                ? side.coverage.endStrike : null,
            rangeMode: side.coverage && side.coverage.kind === 'range' ? mavenRangeMode(side.coverage) : null,
            rangeSize: side.coverage && side.coverage.kind === 'range' && mavenRangeMode(side.coverage) === 'distance'
                ? side.coverage.rangeSize == null ? null : side.coverage.rangeSize : null
        },
        minProfitDollars: mapMinProfit(side.minProfit, defaultQty),
        maxRisk: mapMaxRisk(side.maxRisk, defaultQty)
    };
}

// CR-20260915-001. Override map -> wire array, the ORION (OPG)'s own two filters, restated here
// because Maven's map lives in Maven's Setup (the OPG mapping reads its own seam type):
//   - only EDITED dates cross — a seeded-but-untouched date is being DISPLAYED, not overridden;
//   - only COMPLETE records cross, by the SAME consume-scoped predicate the Setup validator gates
//     Apply on (IV always; a side's vertical skew iff that side is enabled). The validator refuses
//     Apply first; this is the backstop that cannot fire in practice, and the backend's own check
//     refuses the run should it ever pass.
// Null when nothing crosses, so an untouched Setup sends the pre-CR request byte for byte.
function mapMarketOverrides(
    overrides: MarketConditionOverrides | null | undefined,
    needsCallSkew: boolean,
    needsPutSkew: boolean
): OpgCoPilotMarketOverrideDto[] | null {
    if (!overrides || (!needsCallSkew && !needsPutSkew)) {
        return null;
    }
    const out: OpgCoPilotMarketOverrideDto[] = [];
    const keys = Object.keys(overrides);
    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        const o = overrides[key];
        if (!o || !o.edited || !isOverrideComplete(o, needsCallSkew, needsPutSkew)) {
            continue;
        }
        out.push({date: key, ivCall: o.ivCall, ivPut: o.ivPut, vsCall: o.vsCall, vsPut: o.vsPut,
            hsCall: o.hsCall, hsPut: o.hsPut});
    }
    return out.length > 0 ? out : null;
}

export function buildRunCommand(runId: string, request: MavenRunRequest): RunMavenSkill {
    const setup = request.setup;
    const dates = setup.dates;
    const dateFields = mapDateFields(dates ? dates.date : null);
    const defaultQty = request.defaultQty;
    const callsOn = !!(setup.calls && setup.calls.enabled && setup.calls.mode !== 'preset');
    const putsOn = !!(setup.puts && setup.puts.enabled && setup.puts.mode !== 'preset');

    return new RunMavenSkill(
        MAVEN_CONTRACT_VERSION,
        runId,
        request.panelInstanceId,
        setup.portfolioId,
        setup.portfolioOwnerId,
        setup.templateId,
        dateFields.dateKind,
        dateFields.date,
        dateFields.rangeStart,
        dateFields.rangeEnd,
        // Courier fidelity: the exclusions cross exactly as the questionnaire holds them. BR-W2
        // ("a single date takes no exclusions") is the BACKEND's rule to apply — stripping them
        // here would mean two places decide what a single date means, and they would drift.
        (dates ? dates.weekdayExclusions : null) || [],
        toCalendarDateStrings(dates ? dates.specificDateExclusions : null),
        toCalendarDateStrings(dates ? dates.weekdayOverrides : null),
        setup.existingHedges,
        // CR-20260916-003: the Close selection crosses only under Close; Ignore sends two empty
        // arrays, so an Ignore run is the pre-CR request in every field that meant anything.
        setup.existingHedges === 'close' && setup.closeSelection ? (setup.closeSelection.expirations || []).slice() : [],
        setup.existingHedges === 'close' && setup.closeSelection ? (setup.closeSelection.hedgeIds || []).slice() : [],
        mapSideInputs(setup.calls, defaultQty),
        mapSideInputs(setup.puts, defaultQty),
        // Directional bias is NOT passed. S10 is a visible, inert placeholder in iteration 1 and
        // the contract has no field for it (M1 section 8). The questionnaire holds the value; the
        // wire never sees it.
        mapMarketOverrides(setup.marketOverrides, callsOn, putsOn)
    );
}
